import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRightIcon,
  BanknotesIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  ChartPieIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentListIcon,
  CreditCardIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UserGroupIcon,
  VideoCameraIcon,
} from '@heroicons/react/24/outline';

const capabilities = [
  {
    title: 'Organization Command Center',
    description: 'Give directors and admins a single place to manage leagues, teams, age groups, rosters, staff, and parent access.',
    icon: UserGroupIcon,
  },
  {
    title: 'Scheduling and Registration',
    description: 'Publish practices, games, tryouts, camps, and public events with registration workflows built for sports organizations.',
    icon: CalendarDaysIcon,
  },
  {
    title: 'Payments and Invoicing',
    description: 'Collect program fees, subscriptions, team invoices, payment records, and checkout activity without spreadsheet cleanup.',
    icon: CreditCardIcon,
  },
  {
    title: 'Team Communication',
    description: 'Keep coaches, parents, and players aligned with announcements, team chat, notifications, and SMS messaging tools.',
    icon: ChatBubbleLeftRightIcon,
  },
  {
    title: 'Tryouts and Evaluations',
    description: 'Run structured scoring sessions, capture player notes, assign programs, and turn evaluation days into organized decisions.',
    icon: ClipboardDocumentCheckIcon,
  },
  {
    title: 'Video and AI Analysis',
    description: 'Support player development with video workflows and AI-assisted feedback for technique, movement, and performance review.',
    icon: VideoCameraIcon,
  },
  {
    title: 'Budget Management',
    description: 'Track team budgets, collections, outstanding balances, and spend by category, with visibility across every team in the organization.',
    icon: BanknotesIcon,
  },
];

const lifecycleSteps = [
  { title: 'Registration', body: 'Public sign-up and player info.', icon: ClipboardDocumentListIcon },
  { title: 'Evaluations', body: 'Tryout scores and notes.', icon: ClipboardDocumentCheckIcon },
  { title: 'Payments', body: 'Invoices, payment plans, balances.', icon: CreditCardIcon },
  { title: 'Teams', body: 'Placements, rosters, age groups.', icon: UserGroupIcon },
  { title: 'Schedules', body: 'Practices, games, RSVPs.', icon: CalendarDaysIcon },
  { title: 'Messaging', body: 'Coaches, parents, announcements.', icon: ChatBubbleLeftRightIcon },
  { title: 'Budget Management', body: 'Team budgets, collections, reporting.', icon: BanknotesIcon },
];

const aiSpotlightFeatures = [
  { title: 'From Registration to Teams', body: 'Capture sign-ups, evaluations, and place players faster.', icon: UserGroupIcon },
  { title: 'Payments Without Chasing', body: 'Track invoices, plans, and outstanding balances in one place.', icon: BanknotesIcon },
  { title: 'Coach Communication', body: 'Team chat, announcements, and parent updates stay organized.', icon: ChatBubbleLeftRightIcon },
  { title: 'Budget Visibility', body: 'Monitor collections, team budgets, and program health.', icon: ChartPieIcon },
];

const outcomes = [
  'Centralized rosters, schedules, payments, and communication',
  'Public registration pages for events, tryouts, camps, and programs',
  'Coach and admin workflows that scale across multiple teams',
  'Parent-friendly access to messages, invoices, events, and player info',
];

const workflowSteps = [
  {
    label: 'Plan',
    title: 'Set up the season',
    body: 'Create organizations, teams, age groups, programs, events, fees, and access rules.',
  },
  {
    label: 'Run',
    title: 'Operate every week',
    body: 'Coordinate schedules, collect payments, message families, track attendance, and manage rosters.',
  },
  {
    label: 'Improve',
    title: 'Measure and develop',
    body: 'Use reports, evaluations, video, and AI-assisted insights to improve the next decision.',
  },
];

const ProductPreview = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
    <div className="absolute inset-0 bg-gradient-to-b from-white via-emerald-50/70 to-white" />
    <div className="absolute left-1/2 top-24 w-[980px] -translate-x-1/2 rounded-lg border border-gray-200 bg-white/90 shadow-2xl shadow-gray-900/10 backdrop-blur-sm md:top-20">
      <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
        <div className="flex items-center gap-3">
          <img src="/fieldhouse-logo.png" alt="" className="h-10 w-auto" />
          <div>
            <div className="h-3 w-28 rounded bg-gray-900" />
            <div className="mt-2 h-2 w-36 rounded bg-gray-200" />
          </div>
        </div>
        <div className="hidden items-center gap-2 md:flex">
          <div className="h-8 w-24 rounded-md bg-emerald-600/90" />
          <div className="h-8 w-8 rounded-md border border-gray-200 bg-white" />
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4 p-5">
        <div className="col-span-3 hidden space-y-3 border-r border-gray-100 pr-4 md:block">
          {['Dashboard', 'Events', 'Payments', 'Tryouts', 'Reports'].map((item, index) => (
            <div
              key={item}
              className={`h-9 rounded-md ${index === 0 ? 'bg-emerald-100' : 'bg-gray-100'}`}
            />
          ))}
        </div>
        <div className="col-span-12 space-y-4 md:col-span-9">
          <div className="grid grid-cols-3 gap-3">
            {[0, 1, 2].map((index) => (
              <div key={index} className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="h-5 w-10 rounded bg-gray-200" />
                <div className="mt-3 h-2 w-full rounded bg-gray-200" />
                <div className="mt-2 h-2 w-2/3 rounded bg-emerald-200" />
              </div>
            ))}
          </div>

          <div className="grid grid-cols-5 gap-4">
            <div className="col-span-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="mb-4 flex items-center justify-between">
                <div className="h-3 w-28 rounded bg-gray-900" />
                <div className="h-7 w-20 rounded-md bg-sky-100" />
              </div>
              <div className="space-y-3">
                {[82, 66, 74, 48].map((width, index) => (
                  <div key={width} className="flex items-center gap-3">
                    <div className={`h-8 w-8 rounded-md ${index % 2 ? 'bg-amber-100' : 'bg-emerald-100'}`} />
                    <div className="flex-1">
                      <div className="h-2 rounded bg-gray-200" style={{ width: `${width}%` }} />
                      <div className="mt-2 h-2 w-1/2 rounded bg-gray-100" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-span-2 rounded-lg border border-gray-200 bg-gray-950 p-4 text-white">
              <div className="h-3 w-24 rounded bg-white" />
              <div className="mt-5 grid grid-cols-3 gap-2">
                {[1, 2, 3, 4, 5, 6].map((item) => (
                  <div key={item} className="aspect-square rounded-md bg-white/10" />
                ))}
              </div>
              <div className="mt-4 h-2 w-full rounded bg-white/20" />
              <div className="mt-2 h-2 w-2/3 rounded bg-emerald-300" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-white text-gray-950">
      <header className="fixed inset-x-0 top-0 z-40 border-b border-gray-200/80 bg-white/90 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-3" aria-label="FieldHouse home">
            <img src="/fieldhouse-logo.png" alt="FieldHouse" className="h-10 w-auto" />
          </Link>
          <nav className="hidden items-center gap-8 text-sm font-medium text-gray-700 md:flex">
            <a href="#platform" className="hover:text-gray-950">Platform</a>
            <a href="#workflows" className="hover:text-gray-950">Workflows</a>
            <a href="#security" className="hover:text-gray-950">Trust</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" className="rounded-md px-3 py-2 text-sm font-semibold text-gray-700 hover:text-gray-950">
              Sign in
            </Link>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"
            >
              Get started
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </header>

      <main>
        <section className="relative min-h-[760px] overflow-hidden pt-16">
          <ProductPreview />
          <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-white/80 to-white" />
          <div className="relative z-10 mx-auto flex min-h-[700px] max-w-7xl items-center px-4 py-20 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-4 py-2 text-sm font-semibold text-emerald-800 shadow-sm">
                <SparklesIcon className="h-4 w-4" />
                Built for modern sports organizations
              </div>
              <h1 className="max-w-3xl text-5xl font-bold leading-tight tracking-normal text-gray-950 sm:text-6xl lg:text-7xl">
                Run the club from one connected workspace.
              </h1>
              <p className="mt-4 max-w-2xl text-lg font-semibold text-gray-900">
                FieldHouse is a youth sports organization management platform for clubs, leagues, and teams.
              </p>
              <p className="mt-3 max-w-2xl text-lg leading-8 text-gray-700 sm:text-xl">
                Registration, payments, teams, schedules, messaging, budgets, and AI video analysis — all organized in one system.
              </p>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <Link
                  to="/register"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-emerald-600 px-6 py-3 text-base font-semibold text-white shadow-sm hover:bg-emerald-700"
                >
                  Start with FieldHouse
                  <ArrowRightIcon className="h-5 w-5" />
                </Link>
                <Link
                  to="/request-demo"
                  className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-6 py-3 text-base font-semibold text-gray-900 shadow-sm hover:bg-gray-50"
                >
                  Book a demo
                </Link>
              </div>
              <div className="mt-8 grid max-w-2xl gap-3 sm:grid-cols-2">
                {outcomes.map((outcome) => (
                  <div key={outcome} className="flex items-start gap-2 text-sm font-medium text-gray-700">
                    <CheckCircleIcon className="mt-0.5 h-5 w-5 flex-none text-emerald-600" />
                    <span>{outcome}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-gray-200 py-16">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-wide text-emerald-700">The FieldHouse lifecycle</p>
              <h2 className="mt-3 text-3xl font-bold tracking-normal text-gray-950 sm:text-4xl">
                From first sign-up to season-end budget review.
              </h2>
            </div>
            <div className="mt-10 flex flex-wrap items-stretch gap-3">
              {lifecycleSteps.map(({ title, body, icon: Icon }, index) => (
                <React.Fragment key={title}>
                  <div className="flex w-40 flex-col items-start gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:w-44">
                    <div className="flex items-center gap-2">
                      <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-gray-950 text-xs font-bold text-white">
                        {index + 1}
                      </span>
                      <Icon className="h-5 w-5 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-950">{title}</h3>
                      <p className="mt-1 text-xs leading-5 text-gray-600">{body}</p>
                    </div>
                  </div>
                  {index < lifecycleSteps.length - 1 && (
                    <ArrowRightIcon className="hidden h-5 w-5 flex-none self-center text-gray-300 md:block" aria-hidden="true" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </section>

        <section id="platform" className="border-y border-gray-200 bg-gray-50 py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-wide text-emerald-700">Platform</p>
              <h2 className="mt-3 text-3xl font-bold tracking-normal text-gray-950 sm:text-4xl">
                The operational layer for youth and amateur sports.
              </h2>
              <p className="mt-4 text-lg leading-8 text-gray-700">
                FieldHouse brings the daily workflows of a sports organization into one app, so admins have visibility and coaches have tools that fit how teams actually run.
              </p>
            </div>

            <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {capabilities.map(({ title, description, icon: Icon }) => (
                <div key={title} className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                  <div className="flex h-11 w-11 items-center justify-center rounded-md bg-emerald-50 text-emerald-700">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-5 text-lg font-semibold text-gray-950">{title}</h3>
                  <p className="mt-3 text-sm leading-6 text-gray-700">{description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="workflows" className="py-20">
          <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-emerald-700">Connected workflows</p>
              <h2 className="mt-3 text-3xl font-bold tracking-normal text-gray-950 sm:text-4xl">
                Build repeatable systems for every season.
              </h2>
              <p className="mt-4 text-lg leading-8 text-gray-700">
                From preseason registration through final reports, FieldHouse keeps the context in one place. That means fewer handoffs, fewer missed messages, and cleaner data for the next program.
              </p>
            </div>

            <div className="space-y-4">
              {workflowSteps.map((step, index) => (
                <div key={step.label} className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 flex-none items-center justify-center rounded-md bg-gray-950 text-sm font-bold text-white">
                      {index + 1}
                    </div>
                    <div>
                      <p className="text-sm font-semibold uppercase tracking-wide text-emerald-700">{step.label}</p>
                      <h3 className="mt-1 text-xl font-semibold text-gray-950">{step.title}</h3>
                      <p className="mt-2 text-sm leading-6 text-gray-700">{step.body}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-y border-gray-200 bg-gray-50 py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
              <div>
                <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-emerald-600 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">
                  New
                </div>
                <h2 className="text-3xl font-bold tracking-normal text-gray-950 sm:text-4xl">
                  A coach in your pocket.
                </h2>
                <p className="mt-4 text-lg leading-8 text-gray-700">
                  Players and parents can upload video, review mechanics, and get helpful, AI-assisted feedback on technique between practices, lessons, and games.
                </p>
                <Link
                  to="/register"
                  className="mt-6 inline-flex items-center gap-2 text-base font-semibold text-emerald-700 hover:text-emerald-800"
                >
                  Explore AI video analysis
                  <ArrowRightIcon className="h-4 w-4" />
                </Link>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-950 p-6 shadow-lg">
                <div className="flex items-center gap-2 text-sm font-semibold text-white">
                  <VideoCameraIcon className="h-5 w-5 text-emerald-400" />
                  Compare / Overlay
                </div>
                <div className="mt-4 aspect-video rounded-md bg-white/5" />
                <div className="mt-4 h-1.5 w-full rounded-full bg-white/10">
                  <div className="h-1.5 w-2/5 rounded-full bg-emerald-400" />
                </div>
              </div>
            </div>

            <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {aiSpotlightFeatures.map(({ title, body, icon: Icon }) => (
                <div key={title} className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
                  <Icon className="h-6 w-6 text-emerald-600" />
                  <h3 className="mt-3 text-sm font-semibold text-gray-950">{title}</h3>
                  <p className="mt-2 text-sm leading-6 text-gray-700">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="security" className="bg-gray-950 py-20 text-white">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:px-8">
            <div>
              <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-md bg-emerald-500/15 text-emerald-300">
                <ShieldCheckIcon className="h-7 w-7" />
              </div>
              <h2 className="text-3xl font-bold tracking-normal sm:text-4xl">
                Designed for organizations that manage families, minors, money, and trust.
              </h2>
              <p className="mt-5 text-lg leading-8 text-gray-300">
                FieldHouse supports role-based access, parent and guardian workflows, secure payments, privacy-forward AI/video handling, and policies built around youth sports operations.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
              {[
                ['Role-based access', 'Admins, coaches, parents, and players see the right workspace.'],
                ['Payment infrastructure', 'Connect billing, invoices, checkout, and account records.'],
                ['Youth privacy focus', 'Minor data workflows align with FieldHouse privacy and terms policies.'],
                ['Operational reporting', 'Track activity across events, programs, registrations, and teams.'],
              ].map(([title, body]) => (
                <div key={title} className="rounded-lg border border-white/10 bg-white/5 p-5">
                  <div className="flex items-start gap-3">
                    <ChartBarIcon className="mt-1 h-5 w-5 flex-none text-emerald-300" />
                    <div>
                      <h3 className="font-semibold text-white">{title}</h3>
                      <p className="mt-1 text-sm leading-6 text-gray-300">{body}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20">
          <div className="mx-auto max-w-5xl px-4 text-center sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold tracking-normal text-gray-950 sm:text-4xl">
              Give your organization one system to grow around.
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg leading-8 text-gray-700">
              FieldHouse is built for clubs that need a practical operating system, not another isolated app for a single task. Now with AI-powered video analysis to help every player get better.
            </p>
            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              <Link
                to="/register"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-emerald-600 px-6 py-3 text-base font-semibold text-white shadow-sm hover:bg-emerald-700"
              >
                Create an account
                <ArrowRightIcon className="h-5 w-5" />
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-6 py-3 text-base font-semibold text-gray-900 shadow-sm hover:bg-gray-50"
              >
                Sign in
              </Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-gray-200 bg-gray-50">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-8 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <img src="/fieldhouse-logo.png" alt="FieldHouse" className="h-8 w-auto" />
            <span>Built for the modern game.</span>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            <Link to="/sms-opt-in" className="hover:text-gray-950">SMS Opt-In</Link>
            <Link to="/sms-compliance" className="hover:text-gray-950">SMS Compliance</Link>
            <Link to="/privacy-policy" className="hover:text-gray-950">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-gray-950">Terms of Use</Link>
            <Link to="/support" className="hover:text-gray-950">Support</Link>
            <a href="mailto:support@fieldhouse.club" className="hover:text-gray-950">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
